import React from "react"
import { FormGroup, Label, Input, FormFeedback } from "reactstrap"

const TextArea = ({
    label,
    name,
    placeholder,
    validation,
    helperText,
    readOnly = false,
    required = false,
    maxLength,
    rows = 5,
    style,
    className = "",
    handleChange,
    emptyText, // Custom empty text, defaults to "[label] not found"
    ...rest
}) => {
    const showHelperText = !readOnly && (helperText || maxLength)
    const hasError = validation.touched[name] && validation.errors[name]
    const hasValue =
        validation.values[name] && validation.values[name].toString().trim() !== ""
    const currentLength = validation.values[name]?.length || 0
    const remainingChars = maxLength ? maxLength - currentLength : null

    // Determine helper text to show
    const getHelperText = () => {
        if (maxLength) {
            const charText = `${remainingChars} ${remainingChars === 1 ? "character" : "characters"
                } remaining`
            return helperText ? `${helperText} (${charText})` : charText
        }
        return helperText
    }

    // Color for remaining characters
    const getHelperTextColor = () => {
        if (!maxLength) return "text-muted"
        if (remainingChars < 10) return "text-danger"
        if (remainingChars < 30) return "text-warning"
        return "text-muted"
    }

    // Get display value for readonly mode
    const getDisplayValue = () => {
        if (readOnly && !hasValue) {
            const fieldName = label ? label.replace("*", "").trim() : name
            return emptyText || `${fieldName} not found`
        }
        return validation.values[name] || ""
    }

    // Calculate dynamic rows based on content
    const calculateRows = () => {
        const content = getDisplayValue()
        if (!content || content.trim() === "") {
            return 1 // Minimum for empty content
        }

        const lineBreaks = content.split('\n').length
        // Add small buffer for potential line wrapping, cap at reasonable max
        return Math.min(lineBreaks + 1, 10)
    }

    // Use calculated rows for readonly, original rows for editable

    const dynamicRows = readOnly ? calculateRows() : rows

    return (
        <div className="mb-3">
            <FormGroup
                className={`${showHelperText ? "info-text-input-container" : ""
                    } ${className}`}
            >
                {label && (
                    <Label htmlFor={name}>
                        {label}
                        {!readOnly && required ? "*" : ""}
                    </Label>
                )}

                <Input
                    id={name}
                    name={name}
                    type="textarea"
                    placeholder={readOnly ? "" : placeholder}
                    value={getDisplayValue()}
                    onChange={handleChange || validation.handleChange}
                    onBlur={validation.handleBlur}
                    readOnly={readOnly}
                    invalid={hasError ? true : false}
                    maxLength={maxLength}
                    rows={dynamicRows}
                    style={{ height: readOnly ? "auto" : "150px", resize: "none", ...style }}
                    className={`form-control ${readOnly ? "input-as-text" : ""} ${readOnly && !hasValue ? "text-danger" : ""}`}
                    {...rest}
                    spellCheck={true}
                />

                {hasError ? (
                    <FormFeedback type="invalid">{validation.errors[name]}</FormFeedback>
                ) : null}

                {showHelperText && (
                    <span
                        className={`${getHelperTextColor()} text-end ${readOnly ? "d-none" : "d-inline-block"
                            } ${hasError ? "w-50" : "w-100"}`}
                    >
                        {getHelperText()}
                    </span>
                )}
            </FormGroup>
        </div>
    )
}

export default TextArea
